import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CheckCircle, AlertCircle, ImageIcon, Upload, Mail, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiteSettingsSection = ({ settings, language, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState(settings?.notification_email || 'rafid@flowingo.se');
  const [smtpEmail, setSmtpEmail] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Update local state when settings change
  React.useEffect(() => {
    if (settings?.notification_email) {
      setNotificationEmail(settings.notification_email);
    }
  }, [settings]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'sv' ? 'Otillåten filtyp. Använd PNG, JPG, SVG eller WebP.' : 'Invalid file type. Use PNG, JPG, SVG or WebP.');
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(language === 'sv' ? 'Filen är för stor. Max 2MB.' : 'File is too large. Max 2MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(`${API}/settings/logo`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(language === 'sv' ? 'Logotyp uppladdad!' : 'Logo uploaded!');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'sv' ? 'Fel vid uppladdning' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('notification_email', notificationEmail);
      if (smtpEmail.trim()) {
        formData.append('smtp_email', smtpEmail);
      }
      if (smtpPassword.trim()) {
        formData.append('smtp_password', smtpPassword);
      }
      
      await axios.put(`${API}/settings`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(language === 'sv' ? 'E-postinställningar sparade!' : 'Email settings saved!');
      setSmtpEmail('');
      setSmtpPassword('');
      onRefresh();
    } catch (error) {
      toast.error(language === 'sv' ? 'Kunde inte spara inställningar' : 'Could not save settings');
    } finally {
      setSavingEmail(false);
    }
  };

  const currentLogoUrl = settings?.logo_url 
    ? (settings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url)
    : null;

  return (
    <div className="space-y-6">
      {/* Logo Section */}
      <div className="bg-white border border-stone-200 p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-6 flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          {language === 'sv' ? 'Logotyp' : 'Logo'}
        </h3>

        {/* Current Logo Preview */}
        <div className="mb-6">
          <Label className="text-stone-700 mb-3 block">{language === 'sv' ? 'Nuvarande logotyp' : 'Current logo'}</Label>
          <div className="border-2 border-dashed border-stone-200 bg-stone-50 p-8 flex items-center justify-center">
            {currentLogoUrl ? (
              <img 
                src={currentLogoUrl} 
                alt="Current logo" 
                className="max-h-16 max-w-[200px] object-contain"
              />
            ) : (
              <div className="text-center">
                <div className="text-2xl font-bold tracking-tight text-stone-900 mb-2">FLOWINGO</div>
                <p className="text-stone-500 text-sm">{language === 'sv' ? 'Textlogotyp (ingen bild uppladdad)' : 'Text logo (no image uploaded)'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Upload Section */}
        <div className="mb-6">
          <Label className="text-stone-700 mb-3 block">{language === 'sv' ? 'Ladda upp ny logotyp' : 'Upload new logo'}</Label>
          
          {/* Size recommendation */}
          <div className="bg-[#f5f0f2] border border-[#d4c4cc] p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#321a27] mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-stone-900 mb-1">
                  {language === 'sv' ? 'Rekommenderad storlek' : 'Recommended size'}
                </p>
                <ul className="text-sm text-[#321a27] space-y-1">
                  <li>• <strong>{language === 'sv' ? 'Bredd' : 'Width'}:</strong> 180-200 pixlar</li>
                  <li>• <strong>{language === 'sv' ? 'Höjd' : 'Height'}:</strong> 40-50 pixlar</li>
                  <li>• <strong>Format:</strong> PNG ({language === 'sv' ? 'med transparent bakgrund' : 'with transparent background'}) {language === 'sv' ? 'eller' : 'or'} SVG</li>
                  <li>• <strong>Max filstorlek:</strong> 2MB</li>
                </ul>
              </div>
            </div>
          </div>

          {/* File input */}
          <div className="border-2 border-dashed border-stone-300 hover:border-stone-400 transition-colors p-6 text-center">
            <input 
              type="file" 
              id="logo-upload" 
              accept=".png,.jpg,.jpeg,.svg,.webp" 
              onChange={handleLogoUpload} 
              className="hidden"
              disabled={uploading}
            />
            <label htmlFor="logo-upload" className="cursor-pointer">
              <Upload className="w-10 h-10 text-stone-400 mx-auto mb-3" />
              {uploading ? (
                <p className="text-stone-600">{language === 'sv' ? 'Laddar upp...' : 'Uploading...'}</p>
              ) : (
                <>
                  <p className="text-stone-700 font-medium mb-1">
                    {language === 'sv' ? 'Klicka för att ladda upp' : 'Click to upload'}
                  </p>
                  <p className="text-stone-500 text-sm">PNG, JPG, SVG eller WebP</p>
                </>
              )}
            </label>
          </div>
        </div>

        {/* Remove logo option */}
        {currentLogoUrl && (
          <div className="pt-4 border-t border-stone-200">
            <Button 
              variant="outline" 
              className="border-red-300 text-red-700 hover:bg-red-50 rounded-none"
              onClick={async () => {
                if (!window.confirm(language === 'sv' ? 'Vill du ta bort logotypen?' : 'Remove the logo?')) return;
                try {
                  const token = localStorage.getItem('admin_token');
                  const formData = new FormData();
                  formData.append('logo_url', '');
                  await axios.put(`${API}/settings`, formData, {
                    headers: { 
                      'Content-Type': 'multipart/form-data',
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  toast.success(language === 'sv' ? 'Logotyp borttagen' : 'Logo removed');
                  onRefresh();
                } catch (error) {
                  toast.error(language === 'sv' ? 'Kunde inte ta bort logotypen' : 'Could not remove logo');
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {language === 'sv' ? 'Ta bort logotyp' : 'Remove logo'}
            </Button>
          </div>
        )}
      </div>

      {/* Email Notifications Section */}
      <div className="bg-white border border-stone-200 p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-6 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          {language === 'sv' ? 'E-postnotifikationer' : 'Email Notifications'}
        </h3>

        {/* Status indicator */}
        <div className={`p-4 mb-6 border ${settings?.smtp_email ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-2">
            {settings?.smtp_email ? (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-emerald-900">
                  {language === 'sv' ? 'E-post är konfigurerad' : 'Email is configured'}
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-900">
                  {language === 'sv' ? 'SMTP-inställningar saknas' : 'SMTP settings missing'}
                </span>
              </>
            )}
          </div>
          <p className="text-sm mt-1 text-stone-600">
            {language === 'sv' 
              ? 'E-postnotifikationer skickas till rafid@flowingo.se när någon ansöker om jobb eller begär offert.'
              : 'Email notifications are sent to rafid@flowingo.se when someone applies for a job or requests a quote.'
            }
          </p>
        </div>

        <div className="space-y-4">
          {/* SMTP Email */}
          <div>
            <Label className="text-stone-700 mb-2 block">
              {language === 'sv' ? 'Gmail E-postadress' : 'Gmail Email Address'}
            </Label>
            <Input 
              type="email"
              value={smtpEmail}
              onChange={(e) => setSmtpEmail(e.target.value)}
              placeholder="noreply@flowingo.se eller din Gmail"
              className="bg-white border-stone-300"
            />
            <p className="text-sm text-stone-500 mt-1">
              {language === 'sv' 
                ? 'Gmail-adressen som används för att skicka notifikationer'
                : 'Gmail address used to send notifications'
              }
            </p>
          </div>

          {/* SMTP Password */}
          <div>
            <Label className="text-stone-700 mb-2 block">
              {language === 'sv' ? 'Gmail App-lösenord' : 'Gmail App Password'}
            </Label>
            <Input 
              type="password"
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              placeholder={settings?.smtp_email ? '••••••••••••••••' : (language === 'sv' ? 'Ange app-lösenord' : 'Enter app password')}
              className="bg-white border-stone-300"
            />
            <p className="text-sm text-stone-500 mt-1">
              {language === 'sv' 
                ? 'Skapa ett app-lösenord på '
                : 'Create an app password at '
              }
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#321a27] hover:underline">
                Google Account → App passwords
              </a>
            </p>
          </div>

          <Button 
            onClick={handleSaveEmailSettings}
            disabled={savingEmail}
            className="bg-stone-900 text-white hover:bg-stone-800 rounded-none"
          >
            {savingEmail ? '...' : (language === 'sv' ? 'Spara e-postinställningar' : 'Save email settings')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SiteSettingsSection;
